import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { educationList, careerList } from '@/mocks/homeData';

export default function CareerSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="career"
      ref={sectionRef}
      className="relative w-full py-16 md:py-24 bg-background-50"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-4xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block text-accent-600 text-sm font-medium tracking-wider uppercase mb-2">
            Profile
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            {t('career.title')}
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-500">
            신가영 대전이혼전문변호사의 학력 및 경력사항입니다
          </p>
        </div>

        {/* Top: Photo + Education */}
        <div className="flex flex-col md:flex-row gap-6 mb-6 md:mb-8">
          {/* Photo card */}
          <div className="flex-shrink-0 flex justify-center md:justify-start">
            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden shadow-md w-44 h-56 md:w-48 md:h-60">
              <img
                src="/images/ima1.jpg"
                alt="신가영 이혼전문변호사"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Education card */}
          <div className="flex-1 rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-5 md:mb-6">
              <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-accent-100 rounded-full">
                <i className="ri-graduation-cap-line text-accent-700 text-lg md:text-xl"></i>
              </span>
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                {t('career.education')}
              </h3>
              <span className="hidden sm:inline-block h-px flex-1 bg-background-300/50" />
            </div>
            <ul className="space-y-3">
              {educationList.map((item, index) => (
                <li
                  key={index}
                  className="flex items-start gap-3 text-sm md:text-base text-foreground-700"
                >
                  <span className="w-2 h-2 rounded-full bg-accent-500 mt-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom: Career */}
        <div
          className={`bg-background-100 rounded-2xl border border-background-200/70 p-6 md:p-10 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center gap-3 mb-5 md:mb-6">
            <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-primary-100 rounded-full">
              <i className="ri-briefcase-4-line text-primary-700 text-lg md:text-xl"></i>
            </span>
            <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
              {t('career.career')}
            </h3>
            <span className="hidden sm:inline-block h-px flex-1 bg-background-300/50" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {careerList.map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="w-2 h-2 rounded-full bg-primary-500 mt-2 flex-shrink-0" />
                <span className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}